<template>
  <div class="panel-container">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form class="power-form" inline :model="queryForm">
        <el-form-item :label="$str.getKey('数据分析/日期')">
          <el-date-picker v-model="queryForm.timer" @change="timeRangeChange" :disabled-date="disabledDate"
            :range-separator="$str.getKey('数据分析/至')" :clearable="false" type="daterange" format="YYYY-MM-DD"
            size="default" class="filter-date-picker"></el-date-picker>
        </el-form-item>
        <el-form-item :label="$str.getKey('数据分析/能量链')">
          <el-select v-model="queryForm.currELink" :placeholder="$str.getKey('数据分析/请选择能量链')"
            @change="selectedElinkChange">
            <el-option v-for="item in eLinkList" :label="item.label" :value="item.value" :key="item.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$str.getKey('数据分析/eBlock')">
          <el-select v-model="queryForm.currEBlock" :placeholder="$str.getKey('数据分析/请选择能量块')"
            @change="selectedEblockChange">
            <el-option v-for="item in eBlockList" :label="item.label" :value="item.value" :key="item.value"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <el-form-item>
        <div class="filter-actions">
          <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
            <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
            <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
          </v-btn>
        </div>
      </el-form-item>
    </div>
    <div class="main-container">
      <div class="chart-title">{{ $str.getKey('数据分析/电芯温度变化趋势分析曲线') }}</div>
      <div ref="linesChartDom" class="chart-container"></div>
      <!-- 加载状态 -->
      <div v-if="loading" class="chart-status">
        <div class="status-content">
          <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, reactive, onMounted } from "vue";
import { cloneDeep } from "@/utils"
import dayjs from "dayjs";
import { elinkApi } from "@/api/modules/device/index.js";
import { queryBatteryTemperature } from "../api/index.js";
import * as echarts from 'echarts';
import { getChartOption } from "./linesChartOptions.js"
import { useAppStore } from "@/store/modules/app";

const $str = window.$str;
const appStore = useAppStore();
/** 获取stationId，并监听stationId的变化，触发stationIdWatch进行响应 */
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    eLinkList.value = [];
    updateElinkList(stationId.value); // 更新eLink的下拉选项&选中项
  }
})

const debounceTimer = ref(); // 查询防抖
const originQueryForm = {
  timer: [dayjs(), dayjs()],
  currELink: "",
  currEBlock: "",
}
const disabledDate = (date) => {
  return date > Date.now() || date < dayjs().subtract(6, 'month')
}
const eLinkList = ref([]);
const eBlockList = ref([]);
const queryForm = reactive(cloneDeep(originQueryForm));
const loading = ref(false);
const chartEmpty = ref(false);


/** 图表-业务相关变量的定义 ====== */
const linesChartDom = ref(null); // 引用echar-dom
let linesChartObj = null; // 引用echarts实例对象


onMounted(() => {
  /** 初始化图表，执行图表渲染 */
  linesChartObj = echarts.init(linesChartDom.value);
})


/** 时间范围发生变化 */
function timeRangeChange() {
  if (queryForm.timer && queryForm.timer.length === 2) {
    // 确保日期格式正确，转换为 dayjs 对象用于后续处理
    const start = dayjs(queryForm.timer[0]);
    const end = dayjs(queryForm.timer[1]);
    queryForm.timer = [start, end];
    if (queryForm.currEBlock) {
      onSearch();
    }
  }
}

/** 选中的eLink发生变化 */
function selectedElinkChange(type) {
  updateEblockList(type);
}

/** 选中的eBlock发生变化 */
function selectedEblockChange() {
  // onSearch();
}
watch(() => queryForm.currEBlock, (nv, ov) => {
  if (nv) {
    onSearch();
  }
})


/** 查询 */
function onSearch() {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  debounceTimer.value = setTimeout(() => {
    updateChart();
  }, 150);
}

/** 重置 */
function onReset() {
  const originForm = cloneDeep(originQueryForm);
  queryForm.timer = originForm.timer;
  if (eLinkList.value.length > 0) {
    queryForm.currELink = eLinkList.value[0].value;
    updateEblockList(eLinkList.value[0].value); // 更新eBlock的下拉选项&选中项
  } else {
    queryForm.currELink = originForm.currELink;
    queryForm.currEBlock = originForm.currEBlock;
  }
  onSearch();
}

/** 更新eLink的下拉选项&选中项 */
async function updateElinkList(stationId) {
  const list = await elinkApi.getElinkStruct(stationId);
  list.data.forEach(item => {
    eLinkList.value.push({
      label: item.equipmentName,
      value: item.uuid,
      list: item.list
    });
  });
  queryForm.currELink = eLinkList.value[0].value;
  updateEblockList(eLinkList.value[0].value); // 更新eBlock的下拉选项&选中项
}

/** 更新eBlock的下拉选项&选中项 */
function updateEblockList(type) {
  const elink = eLinkList.value.find(elink => elink.value == type);
  // 从elink的数据结构中取出关联的eblock列表
  const selectOptions = [];
  if (elink && elink.list) {
    elink.list.forEach(item => {
      selectOptions.push({
        label: item.equipmentName,
        value: item.uuid
      });
    });
    queryForm.currEBlock = selectOptions[0].value;
  } else {
    queryForm.currEBlock = '';
  }
  eBlockList.value = selectOptions;
}

function updateChart() {
  if (!queryForm.currEBlock) {
    return;
  }
  const params = {
    startTime: dayjs(queryForm.timer[0]).format('YYYY-MM-DD 00:00:00'),
    endTime: dayjs(queryForm.timer[1]).format('YYYY-MM-DD 23:59:59'),
    stationId: stationId.value,
    eBlockId: queryForm.currEBlock
  }
  loading.value = true;
  chartEmpty.value = false;
  queryBatteryTemperature(params).then(res => {
    const chartData = Array.isArray(res.data) ? res.data : [];
    chartEmpty.value = chartData.length === 0;
    // 创建用于图表渲染的配置对象
    const options = getChartOption(chartData, params.startTime, params.endTime);
    // 将配置对象设置给eChart进行渲染
    linesChartObj.clear();
    linesChartObj.setOption(options);
  }).finally(() => {
    loading.value = false;
  })
}

/** 监听浏览器尺寸变化，重置图表尺寸 */
window.addEventListener('resize', () => {
  if (linesChartObj) {
    linesChartObj.resize();
  }
})
</script>
<style lang="scss" scoped>
.panel-container {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;

  .top-container {
    padding: 14px 20px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    border-radius: 20px 20px 0 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;

    .power-form {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {
      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }

      :deep(.el-range-input) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }

      :deep(.el-range-separator) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }
    }

    :deep(.el-select) {
      width: 200px;

      .el-input__wrapper {
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &:hover {
          border-color: #c0cfda;
        }

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      .el-input__inner {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      .el-input__suffix {
        color: rgba(46, 46, 46, 0.5);
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container {
    flex: 1;
    width: 100%;
    padding: 0 20px;
    background: rgba(255, 255, 255, 1);
    border-radius: 0 0 20px 20px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;

    .chart-title {
      font-family: 'HarmonyOS Sans SC', sans-serif;
      font-size: 16px;
      color: rgba(46, 46, 46, 1);
      font-weight: 500;
      margin-bottom: 0;
    }

    .chart-container {
      flex: 1;
      width: 100%;
      min-height: 0;
    }

    .chart-status {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 10;

      .status-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;

        .loading-text,
        .empty-text {
          font-family: "HarmonyOS Sans SC", sans-serif;
          font-size: 14px;
          color: rgba(46, 46, 46, 0.65);
          font-weight: 400;
        }
      }
    }
  }

  // 设置图表tooltip的样式
  ::v-deep .chart-tooltip__time {
    margin-bottom: 8px;
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__items-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .chart-tooltip__series {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    border-radius: 6px;
  }

  ::v-deep .chart-tooltip__series-name,
  ::v-deep .chart-tooltip__value {
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 4px;
    color: var(--text-icon-font-gy-190-primary, rgba(0, 0, 0, 0.90));
  }

  ::v-deep .chart-tooltip__point {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 4px;
    flex-shrink: 0;
  }
}
</style>
